import { Manga, MangaSource } from '../types/manga';
import {
  extractSlug,
  normalizeToMangaUrl,
  cleanTitleForSearch,
  calculateTitleMatchScore,
  parseChapterDate,
  fetch1668ListingHtml,
  findLatestChapterFromPage,
} from './scraper';

export type DiscoverSiteName = 'up-manga.com' | '1668manga.com' | 'mangablackcat.com';

/** One series card on the Discover page, as listed by one site */
export interface DiscoverResult {
  key: string;
  site: DiscoverSiteName;
  url: string;
  title: string;
  image?: string;
  /** Newest chapter number (mangablackcat lists the chapter count instead) */
  latest: number;
  /** Manhwa / Manhua / Manga */
  type?: string;
  nationality?: string;
  rating?: number;
  /** epoch ms of the newest chapter, when the listing shows it */
  updatedAt?: number;
  /** Position in a ranking section */
  rank?: number;
  /** Extra line from the site, e.g. "Action · 110.8K" */
  meta?: string;
  /** The site flags it as just updated (mangablackcat's "UP" badge) */
  fresh?: boolean;
}

export interface DiscoverSection {
  id: string;
  label: string;
  /** Ranking sections are drawn with their position number */
  ranked?: boolean;
  load: (force: boolean) => Promise<DiscoverResult[]>;
}

export interface DiscoverSite {
  name: DiscoverSiteName;
  label: string;
  homeUrl: string;
  dotClass: string;
  sections: DiscoverSection[];
}

const TYPE_TO_FLAG: Record<string, string> = { manhwa: '🇰🇷', manhua: '🇨🇳', manga: '🇯🇵' };

function decodeEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&#8217;/g, '’')
    .replace(/&#8211;/g, '–')
    .replace(/&middot;/g, '·')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

const stripTags = (html: string) => decodeEntities(html.replace(/<[^>]+>/g, ' '));

/** Thumbnail URL -> full size cover ("cover-222x300.png" -> "cover.png") */
const fullSizeImage = (src?: string) => (src ? src.replace(/-\d+x\d+(\.[a-z0-9]+)$/i, '$1') : undefined);

function typeInfo(text: string): { type?: string; nationality?: string } {
  const m = text.match(/\b(Manhwa|Manhua|Manga)\b/i);
  if (!m) return {};
  const type = m[1][0].toUpperCase() + m[1].slice(1).toLowerCase();
  return { type, nationality: TYPE_TO_FLAG[type.toLowerCase()] };
}

/** "Overgeared จ้าวแห่งยุทธภัณฑ์" -> ["Overgeared", "จ้าวแห่งยุทธภัณฑ์"] */
export function splitTitle(title: string): { main: string; sub: string } {
  const i = title.search(/[ก-๙]/);
  if (i > 3) {
    const main = title.slice(0, i).replace(/[\s\-–:|(]+$/, '').trim();
    if (main.length >= 3) return { main, sub: title.slice(i).trim() };
  }
  return { main: title, sub: '' };
}

/** Text between the first start marker and the next end marker found after it */
function region(html: string, start: string | RegExp, ends: (string | RegExp)[]): string {
  const from = typeof start === 'string' ? html.indexOf(start) : html.search(start);
  if (from < 0) return '';
  const rest = html.slice(from);
  let to = rest.length;
  for (const end of ends) {
    const idx = typeof end === 'string' ? rest.indexOf(end, 1) : rest.slice(1).search(end) + 1;
    if (idx > 0 && idx < to) to = idx;
  }
  return rest.slice(0, to);
}

// ---------------------------------------------------------------------------
// Fetching (HTML is cached briefly so sections of the same page share one request)
// ---------------------------------------------------------------------------
const HTML_TTL_MS = 5 * 60 * 1000;
const htmlCache = new Map<string, { at: number; promise: Promise<string> }>();

function cachedHtml(url: string, force: boolean, fetcher: () => Promise<string>): Promise<string> {
  const hit = htmlCache.get(url);
  if (!force && hit && Date.now() - hit.at < HTML_TTL_MS) return hit.promise;
  const promise = fetcher().catch(() => '');
  htmlCache.set(url, { at: Date.now(), promise });
  // A failed load should be retried next time instead of being cached
  promise.then((html) => {
    if (!html) htmlCache.delete(url);
  });
  return promise;
}

/** fresh=false lets the site's own cache answer: up-manga takes ~25s to render a series page uncached */
async function fetchViaProxy(url: string, fresh: boolean = true): Promise<string> {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(`/api/proxy?url=${encodeURIComponent(url)}&nocache=${fresh ? 1 : 0}&_t=${Date.now()}`, {
      signal: controller.signal,
      cache: 'no-store',
    });
    return res.ok ? await res.text() : '';
  } finally {
    clearTimeout(tid);
  }
}

const getProxyHtml = (url: string, force: boolean) => cachedHtml(url, force, () => fetchViaProxy(url));
// 1668manga sits behind Cloudflare Turnstile, so it goes through Jina Reader (rate limited client-side)
const get1668Html = (url: string, force: boolean) => cachedHtml(url, force, () => fetch1668ListingHtml(url));

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/** MangaThemesia ".bsx" cards: popular sliders and up-manga's "อัพเดทใหม่" (.stylefiv) list */
function parseBsxCards(html: string, site: DiscoverSiteName): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  for (const block of html.split(/<div class=["']bsx["']>/i).slice(1)) {
    const href = block.match(/href=["'](https?:\/\/[^"']+)["']/i);
    if (!href) continue;
    const url = normalizeToMangaUrl(href[1]) || href[1];
    const ttDiv = block.match(/<div class=["']tt["'][^>]*>([\s\S]*?)<\/div>/i);
    const titleAttr = block.match(/title=["']([^"']+)["']/i);
    const title = ttDiv ? stripTags(ttDiv[1]) : decodeEntities(titleAttr?.[1] || '');
    if (!title) continue;

    const img = block.match(/<img[^>]*src=["']([^"']+)["']/i);
    const typename = block.match(/class=["']type(?:name)?\s+(\w+)["']/i);
    const epxs = block.match(/<div class=["']epxs["'][^>]*>\s*(?:ตอนที่?|chapter|ch\.?)?\s*(\d+(?:\.\d+)?)/i);
    const fivChap = block.match(/class=["']fivchap["'][^>]*>\s*(?:ตอนที่?)?\s*(\d+(?:\.\d+)?)/i);
    const fivTime = block.match(/class=["']fivtime["'][^>]*>([^<]+)</i);
    const score = block.match(/class=["']numscore["'][^>]*>\s*([\d.]+)/i);

    out.push({
      key: `${site}|${url}`,
      site,
      url,
      title,
      image: fullSizeImage(img?.[1]),
      latest: parseFloat((fivChap || epxs)?.[1] || '0') || 0,
      ...typeInfo(typename?.[1] || ''),
      rating: score ? parseFloat(score[1]) || undefined : undefined,
      // up-manga writes "2 ชั่วโมง" without "ที่แล้ว"
      updatedAt: fivTime ? parseChapterDate(`${fivTime[1].trim()} ที่แล้ว`) || undefined : undefined,
    });
  }
  return out;
}

/** 1668manga homepage "อัพเดทตอนล่าสุด" (.uta) blocks */
function parseUtaBlocks(html: string, site: DiscoverSiteName): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  for (const block of html.split(/<div class=["']uta["']>/i).slice(1)) {
    const href = block.match(/href=["'](https?:\/\/[^"']+\/manga\/[^"']+)["']/i);
    if (!href) continue;
    const url = normalizeToMangaUrl(href[1]) || href[1];
    const h4 = block.match(/<h4[^>]*>([\s\S]*?)<\/h4>/i);
    const titleAttr = block.match(/title=["']([^"']+)["']/i);
    const title = h4 ? stripTags(h4[1]) : decodeEntities(titleAttr?.[1] || '');
    if (!title) continue;
    const img = block.match(/<img[^>]*src=["']([^"']+)["']/i);
    const ulType = block.match(/<ul class=["'](\w+)["']/i);
    // Newest chapter first: <li><a>ตอนที่ 204</a><span>26 seconds ago</span></li>
    const first = block.match(/<li>\s*<a[^>]*>\s*(?:ตอนที่?|chapter|ch\.?)?\s*(\d+(?:\.\d+)?)[^<]*<\/a>\s*(?:<span>([^<]*)<\/span>)?/i);
    const status = block.match(/class=["']statusind[^"']*["'][^>]*>([\s\S]*?)<\/span>/i);

    out.push({
      key: `${site}|${url}`,
      site,
      url,
      title,
      image: fullSizeImage(img?.[1]),
      latest: first ? parseFloat(first[1]) || 0 : 0,
      ...typeInfo(ulType?.[1] || ''),
      updatedAt: first?.[2] ? parseChapterDate(first[2]) || undefined : undefined,
      meta: status ? stripTags(status[1]) : undefined,
    });
  }
  return out;
}

/** mangablackcat <article> cards (ranking, popular and latest lists) */
function parseBlackcatArticles(html: string): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  const seen = new Set<string>();
  for (const block of html.split(/<article\b[^>]*>/i).slice(1)) {
    const href = block.match(/href=["'](https:\/\/mangablackcat\.com\/manga\/[^"'\/?#]+)["']/i);
    if (!href) continue;
    const url = `${href[1]}?sort=desc`;
    if (seen.has(url)) continue;
    const h3 = block.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i);
    if (!h3) continue;
    seen.add(url);
    // Recently updated series carry an "UP" badge inside the <h3>, before the title
    const upBadge = /^\s*UP\b/.test(stripTags(h3[1]));

    const img = block.match(/<img[^>]*src=["']([^"']+)["']/i);
    const figure = block.match(/<figure[\s\S]*?<\/figure>/i)?.[0] || '';
    const chapters = figure.match(/<span[^>]*>\s*(\d+(?:\.\d+)?)\s*ตอน\s*<\/span>/i);
    const rank = chapters ? null : figure.match(/<span[^>]*>\s*(\d{1,3})\s*<\/span>/i);
    const p = block.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
    const meta = p ? stripTags(p[1]).replace(/\s*·\s*/g, ' · ') : undefined;

    out.push({
      key: `mangablackcat.com|${url}`,
      site: 'mangablackcat.com',
      url,
      title: stripTags(h3[1]).replace(/^UP\s+/, ''),
      image: img?.[1],
      // "233 ตอน" is how many chapters there are, not the newest number (which can be lower, e.g. 227);
      // leaving it 0 makes the page look up the real one from the series page
      latest: 0,
      ...typeInfo(meta || ''),
      rank: rank ? parseInt(rank[1], 10) : undefined,
      meta,
      fresh: upBadge || undefined,
    });
  }
  return out;
}

/** MangaThemesia sidebar ranking (.serieslist.pop / wpop-weekly|monthly|alltime) */
function parseWpopList(html: string, site: DiscoverSiteName): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  for (const li of html.split(/<li>/i).slice(1)) {
    const ctr = li.match(/class=["']ctr["'][^>]*>\s*(\d+)/i);
    const href = li.match(/href=["'](https?:\/\/[^"']+)["']/i);
    if (!ctr || !href) continue;
    const url = normalizeToMangaUrl(href[1]) || href[1];
    const img = li.match(/<img[^>]*src=["']([^"']+)["']/i);
    const left = li.match(/class=["']leftseries["'][^>]*>[\s\S]*?<a[^>]*>([\s\S]*?)<\/a>/i);
    const imgTitle = li.match(/<img[^>]*title=["']([^"']+)["']/i);
    const title = left ? stripTags(left[1]) : decodeEntities(imgTitle?.[1] || '');
    if (!title) continue;
    const genres = li.match(/<span>\s*<b>[^<]*<\/b>\s*:?\s*([\s\S]*?)<\/span>/i);
    const score = li.match(/class=["']numscore["'][^>]*>\s*([\d.]+)/i);
    out.push({
      key: `${site}|${url}`,
      site,
      url,
      title,
      image: fullSizeImage(img?.[1]),
      latest: 0,
      rank: parseInt(ctr[1], 10),
      rating: score ? parseFloat(score[1]) || undefined : undefined,
      meta: genres ? stripTags(genres[1]).replace(/\s+,/g, ',') : undefined,
    });
  }
  return out;
}

/** 1668manga "มังงะอัพเดท" / "มังฮวาอัพเดท" / "แนะนำสุ่ม" cards, which link to a chapter page */
function parse1668ChapterCards(html: string): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  for (const chunk of html.split(/<a\s+href=/i).slice(1)) {
    const href = chunk.match(/^["']([^"']+)["']/);
    const titleAttr = chunk.match(/title=["']([^"']+)["']/i);
    if (!href || !titleAttr || !/ตอนที่/.test(titleAttr[1])) continue;
    const url = normalizeToMangaUrl(href[1]) || href[1];
    if (out.some((r) => r.url === url)) continue;
    const full = decodeEntities(titleAttr[1]);
    const ch = full.match(/ตอนที่\s*(\d+(?:\.\d+)?)/);
    const img = chunk.match(/<img[^>]*src=["']([^"']+)["']/i);
    const ago = chunk.match(/(\d+\s*(?:seconds?|minutes?|hours?|days?|weeks?|months?|years?)\s+ago)/i);
    out.push({
      key: `1668manga.com|${url}`,
      site: '1668manga.com',
      url,
      title: full.replace(/\s*ตอนที่\s*[\d.]+.*$/, '').trim(),
      image: fullSizeImage(img?.[1]),
      latest: ch ? parseFloat(ch[1]) : 0,
      updatedAt: ago ? parseChapterDate(ago[1]) || undefined : undefined,
    });
  }
  return out;
}

/** mangablackcat hero slider: a series link followed by its cover <img alt="title"> */
function parseBlackcatHero(html: string): DiscoverResult[] {
  const out: DiscoverResult[] = [];
  const links = [...html.matchAll(/href=["'](https:\/\/mangablackcat\.com\/manga\/[^"'\/?#]+)["']/gi)];
  links.forEach((m, i) => {
    const url = `${m[1]}?sort=desc`;
    if (out.some((r) => r.url === url)) return;
    const chunk = html.slice(m.index, links[i + 1]?.index ?? html.length);
    const img = chunk.match(/<img[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']*)["']/i);
    if (!img || !img[2]) return;
    out.push({
      key: `mangablackcat.com|${url}`,
      site: 'mangablackcat.com',
      url,
      title: decodeEntities(img[2]),
      image: img[1],
      latest: 0,
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// Site definitions: every section of each site's homepage, in page order,
// with its latest-updates list loaded for pages 1-3
// ---------------------------------------------------------------------------
const UP_HOME = 'https://www.up-manga.com/';
const upPage = (n: number) => (n === 1 ? UP_HOME : `https://www.up-manga.com/page/${n}/`);
const C1668_HOME = 'https://1668manga.com/';
const c1668Page = (n: number) => (n === 1 ? C1668_HOME : `https://1668manga.com/page/${n}/`);
const MBC_HOME = 'https://mangablackcat.com/';
const mbcLatest = (n: number) => `https://mangablackcat.com/latest?page=${n}`;

const PAGES = [1, 2, 3];
const RANKING_RANGES = ['weekly', 'monthly', 'alltime'] as const;

export const DISCOVER_SITES: DiscoverSite[] = [
  {
    name: 'up-manga.com',
    label: 'up-manga',
    homeUrl: UP_HOME,
    dotClass: 'bg-orange-500',
    sections: [
      {
        id: 'popular',
        label: 'อ่านมังงะยอดนิยม',
        load: async (force) =>
          parseBsxCards(region(await getProxyHtml(UP_HOME, force), 'popularslider', ['stylefiv', '<div class="releases']), 'up-manga.com'),
      },
      ...PAGES.map((n) => ({
        id: `latest-${n}`,
        label: `มังงะอัพเดทใหม่ หน้า ${n}`,
        load: async (force: boolean) =>
          parseBsxCards(
            region(await getProxyHtml(upPage(n), force), /<div class=["']bs styletere stylefiv/, ['class="hpage"', 'id="sidebar"']),
            'up-manga.com'
          ),
      })),
      ...RANKING_RANGES.map((range) => ({
        id: `hot-${range}`,
        label: `มังงะยอดฮิต ${{ weekly: 'รายสัปดาห์', monthly: 'รายเดือน', alltime: 'ตลอดกาล' }[range]}`,
        ranked: true,
        load: async (force: boolean) =>
          parseWpopList(region(await getProxyHtml(UP_HOME, force), `wpop-${range}`, ['</ul>']), 'up-manga.com'),
      })),
    ],
  },
  {
    name: '1668manga.com',
    label: '1668manga',
    homeUrl: C1668_HOME,
    dotClass: 'bg-sky-500',
    sections: [
      {
        id: 'popular',
        label: 'มังงะ ยอดนิยม',
        load: async (force) =>
          parseBsxCards(
            region(await get1668Html(C1668_HOME, force), 'popularslider', ['อัพเดทตอนล่าสุด', '<div class="uta">']),
            '1668manga.com'
          ),
      },
      ...PAGES.map((n) => ({
        id: `latest-${n}`,
        label: `อัพเดทตอนล่าสุด หน้า ${n}`,
        load: async (force: boolean) => parseUtaBlocks(await get1668Html(c1668Page(n), force), '1668manga.com'),
      })),
      {
        id: 'manga-updates',
        label: 'มังงะอัพเดท',
        load: async (force) =>
          parse1668ChapterCards(region(await get1668Html(C1668_HOME, force), /<h2[^>]*>\s*มังงะอัพเดท/, [/<h2[^>]*>\s*มังฮวาอัพเดท/])),
      },
      {
        id: 'manhwa-updates',
        label: 'มังฮวาอัพเดท',
        load: async (force) =>
          parse1668ChapterCards(region(await get1668Html(C1668_HOME, force), /<h2[^>]*>\s*มังฮวาอัพเดท/, [/<h2[^>]*>\s*แนะนำสุ่ม/])),
      },
      {
        id: 'random',
        label: 'แนะนำสุ่ม',
        load: async (force) =>
          parse1668ChapterCards(
            region(await get1668Html(C1668_HOME, force), /<h2[^>]*>\s*แนะนำสุ่ม/, [/<h3[^>]*>\s*มังงะยอดนิยม/, 'id="sidebar"'])
          ),
      },
      ...RANKING_RANGES.map((range) => ({
        id: `hot-${range}`,
        label: `มังงะยอดนิยม ${{ weekly: 'Weekly', monthly: 'Monthly', alltime: 'All' }[range]}`,
        ranked: true,
        load: async (force: boolean) =>
          parseWpopList(region(await get1668Html(C1668_HOME, force), `wpop-${range}`, ['</ul>']), '1668manga.com'),
      })),
    ],
  },
  {
    name: 'mangablackcat.com',
    label: 'mangablackcat',
    homeUrl: MBC_HOME,
    dotClass: 'bg-slate-200',
    sections: [
      {
        id: 'hero',
        label: 'ไฮไลต์',
        load: async (force) => parseBlackcatHero(region(await getProxyHtml(MBC_HOME, force), '<body', [/<h2[^>]*>\s*แนะนำ/])),
      },
      {
        id: 'recommended',
        label: 'แนะนำ',
        load: async (force) =>
          parseBlackcatArticles(region(await getProxyHtml(MBC_HOME, force), /<h2[^>]*>\s*แนะนำ/, [/<h2[^>]*>\s*จัดอันดับ/])),
      },
      {
        id: 'trending',
        label: 'จัดอันดับ มาแรง',
        ranked: true,
        load: async (force) =>
          parseBlackcatArticles(
            region(await getProxyHtml(MBC_HOME, force), `x-show="tab === 'trending'"`, [`x-show="tab === 'popular'"`])
          ),
      },
      {
        id: 'hot',
        label: 'จัดอันดับ ยอดฮิต',
        ranked: true,
        load: async (force) =>
          parseBlackcatArticles(region(await getProxyHtml(MBC_HOME, force), `x-show="tab === 'popular'"`, [/<h2[^>]*>\s*ยอดนิยม/])),
      },
      {
        id: 'popular',
        label: 'ยอดนิยม',
        load: async (force) =>
          parseBlackcatArticles(
            region(await getProxyHtml(MBC_HOME, force), /<h2[^>]*>\s*ยอดนิยม/, ['ค้นหาจากหมวดหมู่', /<h2[^>]*>\s*อัพเดทล่าสุด/])
          ),
      },
      ...PAGES.map((n) => ({
        id: `latest-${n}`,
        label: `อัพเดทล่าสุด หน้า ${n}`,
        load: async (force: boolean) =>
          parseBlackcatArticles(
            region(await getProxyHtml(mbcLatest(n), force), /<h[12][^>]*>\s*อัพเดทล่าสุด/, [/<h[12][^>]*>\s*สำรวจ/])
          ),
      })),
    ],
  },
];

/**
 * Newest chapter from the series page, for cards whose section doesn't list one (rankings, sliders).
 * 1668manga is skipped: its pages go through the rate-limited Jina Reader.
 */
export async function fetchSeriesLatest(result: DiscoverResult): Promise<number> {
  if (result.site === '1668manga.com') return 0;
  // A slightly stale chapter count is fine for browsing, so the site's cached page is used
  for (let attempt = 0; attempt < 2; attempt++) {
    const html = await cachedHtml(`cached:${result.url}`, false, () => fetchViaProxy(result.url, false));
    const latest = findLatestChapterFromPage(html, result.url);
    if (latest > 0) return latest;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Search within one site
// ---------------------------------------------------------------------------
const SEARCH_ENDPOINTS: Record<DiscoverSiteName, { path: string; timeoutMs: number }> = {
  'up-manga.com': { path: '/api/search-upmanga', timeoutMs: 6000 },
  'mangablackcat.com': { path: '/api/search-mangablackcat', timeoutMs: 6000 },
  // Goes through Jina Reader, so it is the slow one
  '1668manga.com': { path: '/api/search-1668', timeoutMs: 15000 },
};

export async function searchSite(site: DiscoverSiteName, query: string): Promise<DiscoverResult[]> {
  const q = cleanTitleForSearch(query);
  const { path, timeoutMs } = SEARCH_ENDPOINTS[site];
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  let list: Array<{ title: string; link: string; latest: number; image?: string }> = [];
  try {
    const res = await fetch(`${path}?q=${encodeURIComponent(q)}`, { signal: controller.signal, cache: 'no-store' });
    if (res.ok) list = await res.json();
  } catch {
  } finally {
    clearTimeout(tid);
  }

  const seen = new Set<string>();
  const results: DiscoverResult[] = [];
  for (const item of list) {
    if (!item.link || !item.title) continue;
    const url = site === 'mangablackcat.com' ? item.link : normalizeToMangaUrl(item.link) || item.link;
    if (seen.has(url)) continue;
    seen.add(url);
    results.push({
      key: `${site}|${url}`,
      site,
      url,
      title: decodeEntities(item.title),
      image: item.image || undefined,
      latest: item.latest || 0,
    });
  }
  // Best title match first
  return results
    .map((r) => ({ r, s: calculateTitleMatchScore(q, r.title).score }))
    .sort((a, b) => b.s - a.s)
    .map((x) => x.r);
}

// ---------------------------------------------------------------------------
// Matching against the user's list, and building a new entry
// ---------------------------------------------------------------------------

/** Latin part of a title ("Nano Machine นาโนมาชิน" -> "nano machine"), else the whole normalized title */
export function discoverTitleKey(title: string): string {
  const latin = title.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (latin.replace(/\s/g, '').length >= 3) return latin;
  return title.toLowerCase().replace(/[^a-z0-9ก-๙]+/g, ' ').trim();
}

function siteOfUrl(url: string): DiscoverSiteName | null {
  if (url.includes('up-manga.com')) return 'up-manga.com';
  if (url.includes('1668manga.com')) return '1668manga.com';
  if (url.includes('mangablackcat.com')) return 'mangablackcat.com';
  return null;
}

const slugKey = (url: string) => (extractSlug(url) || '').toLowerCase();

function keysMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  // Sites repeat parts of a name differently: "99 wooden stick" vs "99 wooden stick 99". Only words the
  // shorter title already has may be extra, so a sequel ("solo leveling ragnarok") stays separate.
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length < 8 || !long.startsWith(`${short} `)) return false;
  const shortWords = new Set(short.split(' '));
  return long
    .slice(short.length + 1)
    .split(' ')
    .every((w) => shortWords.has(w));
}

/** The tracked manga this result already is, if any (same site + slug, or same title) */
export function findTrackedManga(result: DiscoverResult, mangaList: Manga[]): Manga | undefined {
  const hitKey = `${result.site}|${slugKey(result.url)}`;
  const titleKey = discoverTitleKey(result.title);
  return mangaList.find((m) => {
    if ((m.sources || []).some((s) => `${siteOfUrl(s.url)}|${slugKey(s.url)}` === hitKey)) return true;
    return keysMatch(titleKey, discoverTitleKey(m.title));
  });
}

export function buildMangaFromDiscover(
  result: DiscoverResult,
  options: { title?: string; grade: Manga['grade']; readCh: number; nationality?: string },
  idSuffix: number = 0
): Manga {
  // The listing's date belongs to its newest chapter, so the card can show when it came out right away
  const chapterAt = result.latest > 0 ? result.updatedAt : undefined;
  const source: MangaSource = { name: result.site, url: result.url, chapter: result.latest || 0, chapterAt };
  return {
    id: `ID_${Date.now()}${idSuffix ? `_${idSuffix}` : ''}`,
    title: options.title?.trim() || result.title,
    grade: options.grade,
    status: 'ยังไม่จบ',
    nationality: options.nationality || result.nationality || '🇰🇷',
    currentCh: Math.max(result.latest || 0, options.readCh),
    readCh: options.readCh,
    imageUrl: result.image || '',
    sources: [source],
    latestSourceUrl: source.url,
    latestSourceName: source.name,
    order: 999999,
    lastUpdatedAt: '',
    latestChapterAt: chapterAt,
  };
}
